import { Icon } from "@zapier/zinnia-icons";
import {
  Alert,
  Button,
  LabeledCheckbox,
  Surface,
  Text,
} from "@zapier/design-system";
import { useEffect, useState } from "react";

import type { PromptPlan } from "../../types/PromptPlan";

import { AppSelection } from "./AppSelection";
import styles from "./AiPromptBuilder.module.css";

/**
 * Splits text by integration name, handling singular/plural variations
 * @param text - The text to split
 * @param integrationName - The integration name to search for
 * @returns Object with parts array and the matched text
 */
const splitByIntegration = (
  text: string,
  integrationName: string,
): { parts: string[]; matchedText: string } => {
  // Safety check for undefined integrationName since it's streamed in
  if (!integrationName) {
    return { parts: [text], matchedText: "" };
  }

  // Try exact match first, then handle singular/plural variations
  if (text.includes(integrationName)) {
    return { parts: text.split(integrationName), matchedText: integrationName };
  } else if (integrationName.endsWith("s")) {
    const singular = integrationName.slice(0, -1); // "Zapier Agents" -> "Zapier Agent"
    if (text.includes(singular)) {
      return { parts: text.split(singular), matchedText: singular };
    }
  }

  return { parts: [text], matchedText: "" };
};

interface AiPromptBuilderProps {
  originalPlan: PromptPlan | null;
  isStreaming: boolean;
  handleSubmit: (prompt: string) => void;
  emitUserInteractionEvent: (event: {
    eventAction: string;
    interactionGoal: string;
    rawText?: string;
  }) => void;
  showAgentsNodes: boolean;
}

// Component to highlight integration names and make them interactive
const PlanComponentItem = ({
  component,
  planComponentIndex,
  updatedPlan,
  setUpdatedPlan,
  emitUserInteractionEvent,
  showAgentsNodes,
}: {
  component: any;
  planComponentIndex: number;
  updatedPlan: PromptPlan | null;
  setUpdatedPlan: React.Dispatch<React.SetStateAction<PromptPlan | null>>;
  emitUserInteractionEvent: (event: {
    eventAction: string;
    interactionGoal: string;
    rawText?: string;
  }) => void;
  showAgentsNodes: boolean;
}) => {
  const handleIntegrationClick = (newApp: any, oldApp: any) => {
    if (!updatedPlan) return;

    // Create a deep copy of the updatedPlan
    const newPlan = JSON.parse(JSON.stringify(updatedPlan));

    // Update the integration in the plan components
    const planComponent = newPlan.planComponents[planComponentIndex];
    const integration = planComponent.integrations.find(
      (integration: any) => integration.name === oldApp.name,
    );

    if (integration) {
      // Replace the old integration with the new one
      integration.name = newApp.name;

      // Update the description text to replace the old app name with the new app name
      planComponent.description = planComponent.description.replace(
        new RegExp(oldApp.name, "g"),
        newApp.name,
      );

      setUpdatedPlan(newPlan);
    }
  };

  // If no integrations are present, return the full description
  if (!component.integrations || component.integrations.length === 0) {
    return <Text>{component.description}</Text>;
  }

  // Process description text to highlight integrations
  let segments: React.ReactNode[] = [component.description];

  component.integrations.forEach((integration: any) => {
    const newSegments: React.ReactNode[] = [];

    segments.forEach((segment) => {
      if (typeof segment !== "string") {
        newSegments.push(segment);
        return;
      }

      const { parts, matchedText } = splitByIntegration(
        segment,
        integration.name,
      );
      // If integration name not found in this segment, just keep it as is
      if (parts.length === 1) {
        newSegments.push(segment);
        return;
      }

      // Otherwise, split and insert clickable spans
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) {
          newSegments.push(
            <AppSelection
              emitUserInteractionEvent={emitUserInteractionEvent}
              key={`${integration.name}-${i}`}
              app={integration}
              setApp={(newApp) => handleIntegrationClick(newApp, integration)}
              showAgentsNodes={showAgentsNodes}
              displayText={matchedText} // Use matched text to preserve natural language (e.g., "Zapier Agent" vs "Zapier Agents")
            />,
          );
        }

        if (parts[i]) {
          newSegments.push(parts[i]);
        }
      }
    });

    segments = newSegments;
  });

  return <Text>{segments}</Text>;
};

export function AiPromptBuilder({
  handleSubmit,
  isStreaming,
  originalPlan,
  emitUserInteractionEvent,
  showAgentsNodes,
}: AiPromptBuilderProps) {
  const [building, setBuilding] = useState(false);
  const [rejectedComponents, setRejectedComponents] = useState<string[]>([]);
  const [additionalComponents, setAdditionalComponents] = useState<string[]>(
    [],
  );

  const [updatedPlan, setUpdatedPlan] = useState<PromptPlan | null>(null);

  useEffect(() => {
    if (!isStreaming && originalPlan) {
      setUpdatedPlan(originalPlan);
    }
  }, [originalPlan, isStreaming]);

  if (!originalPlan) return null;

  const plan = updatedPlan || originalPlan;

  const handleBuild = async () => {
    setBuilding(true);
    const prompt = `Here is the plan: ${plan.planComponents
      .map((component) => `- ${component.description}`)
      .join("\n")}
      ${additionalComponents.map((component) => `- ${component}`).join("\n")}`;

    handleSubmit(prompt);

    await emitUserInteractionEvent({
      eventAction: "click",
      interactionGoal: "Built with an enriched prompt",
    });
  };

  const visiblePlanComponents = plan?.planComponents?.filter(
    (component) => !rejectedComponents.includes(component.description),
  );

  const handleRemoveComponent = (description: any) => {
    setRejectedComponents([...rejectedComponents, description]);
  };

  const renderPlanComponents = () => {
    if (!visiblePlanComponents) return null;

    if (visiblePlanComponents.length === 0) {
      return (
        <div className={styles.planComponent}>
          <Alert>
            <Text type="Body1" color="TextWeaker">
              You have removed all the components from the plan. You may want to
              try entering another prompt to generate a new plan.
            </Text>
          </Alert>
        </div>
      );
    }

    return visiblePlanComponents?.map((component, index) => (
      <div
        key={`${component.description}-${index}`}
        className={styles.planComponent}
      >
        <Surface borderColor="StrokeDefault">
          <div className={styles.systemComponentType}>
            <PlanComponentItem
              emitUserInteractionEvent={emitUserInteractionEvent}
              component={component}
              planComponentIndex={index}
              updatedPlan={updatedPlan}
              setUpdatedPlan={setUpdatedPlan}
              showAgentsNodes={showAgentsNodes}
            />
            <button
              className={styles.removeButton}
              onClick={() => handleRemoveComponent(component.description)}
            >
              <Icon name="formXCircle" color="GrayWarm5" />
            </button>
          </div>
        </Surface>
      </div>
    ));
  };

  return (
    <div className={styles.container}>
      <div className={styles.nextStepsSection}>
        <div className={styles.sectionHeader}>
          <Text type="ParagraphHeader3">Planning your system</Text>
          <div className={styles.planText}>
            <Text type="Body1" color="TextWeaker">
              {plan.plan}
            </Text>
          </div>
        </div>
        <Surface>{renderPlanComponents()}</Surface>
      </div>
      {plan?.potentialNextSteps?.length > 0 && (
        <div className={styles.nextStepsSection}>
          <div className={styles.sectionHeader}>
            <Text type="ParagraphHeader3">Additional steps to consider</Text>
          </div>
          {plan?.potentialNextSteps?.map((step, index) => (
            <div key={`${step}-${index}`} className={styles.planComponent}>
              <Surface borderColor="StrokeDefault">
                <LabeledCheckbox
                  checked={additionalComponents.includes(step.description)}
                  onChange={() => {
                    if (additionalComponents.includes(step.description)) {
                      setAdditionalComponents(
                        additionalComponents.filter(
                          (component) => component !== step.description,
                        ),
                      );
                    } else {
                      setAdditionalComponents([
                        ...additionalComponents,
                        step.description,
                      ]);
                    }
                  }}
                >
                  <Text
                    color={
                      additionalComponents.includes(step.description)
                        ? "TextDefault"
                        : "TextWeaker"
                    }
                    type={
                      additionalComponents.includes(step.description)
                        ? "Body2"
                        : "Body1"
                    }
                  >
                    {step.description}
                  </Text>
                </LabeledCheckbox>
              </Surface>
            </div>
          ))}
        </div>
      )}
      {!isStreaming && (
        <div className={styles.buttonWrapper}>
          <Button
            size="large"
            variant="primary"
            onPress={handleBuild}
            isPending={building}
          >
            Start building
          </Button>
        </div>
      )}
    </div>
  );
}
